import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';

export interface NotificationQueryArgs {
  page?: number;
  size?: number;
  unreadOnly?: boolean;
  userId: string;
}

export interface INotificationContent {
  content: any[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

export const notificationApi = createApi({
  tagTypes: ['NOTIFICATION_LIST', 'UNREAD_NOTIFICATION_COUNT'],
  reducerPath: 'notificationApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    notificationList: builder.infiniteQuery<INotificationContent, NotificationQueryArgs, number>({
      infiniteQueryOptions: {
        // Must provide a default initial page param value
        initialPageParam: 0,
        // Optionally limit the number of cached pages
        maxPages: 3,
        // Must provide a `getNextPageParam` function
        getNextPageParam: (lastPage, allPages, lastPageParam, _allPageParams) => {
          return lastPageParam < lastPage.totalPages - 1 ? lastPageParam + 1 : undefined;
        },
        // Optionally provide a `getPreviousPageParam` function
        getPreviousPageParam: (firstPage, allPages, firstPageParam, _allPageParams) => {
          return firstPageParam > 0 ? firstPageParam - 1 : undefined;
        },
      },
      // The `query` function receives `{queryArg, pageParam}` as its argument
      query: ({ queryArg, pageParam }) => {
        const { userId, size } = queryArg;
        const sortParam = `&sort=read,asc&sort=createdDate,desc`;
        return {
          url: `/nss/api/notification?userId=${userId}${sortParam}`,
          params: {
            page: pageParam,
            size,
          },
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content;
        if (Array.isArray(content)) {
          content[0].notifications = (content[0].notifications || []).map((item: any) => ({
            ...item,
            user: content[0].managers?.[item.createdBy] || {},
          }));
        }
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['NOTIFICATION_LIST'],
    }),
    notificationUpdateRead: builder.mutation<any, { id: string; isRead: boolean }>({
      query: ({ id, isRead }) => ({
        url: `/nss/api/notification?id=${id}&isRead=${isRead}`,
        method: 'put',
      }),
      invalidatesTags: () => ['NOTIFICATION_LIST', 'UNREAD_NOTIFICATION_COUNT'],
    }),
    notificationUpdateAllRead: builder.mutation<any, string>({
      query: (userId) => ({
        url: `/nss/api/notification/mark-all-read?userId=${userId}`,
        method: 'put',
        body: {},
      }),
      invalidatesTags: () => ['NOTIFICATION_LIST', 'UNREAD_NOTIFICATION_COUNT'],
    }),
    notificationGetUnreadCount: builder.query<any, string>({
      query: (userID) => ({
        url: `/nss/api/notification/unread-count?userId=${userID}`,
      }),
      providesTags: () => ['UNREAD_NOTIFICATION_COUNT'],
    }),
  }),
});

export const {
  useNotificationListInfiniteQuery,
  useNotificationUpdateReadMutation,
  useNotificationGetUnreadCountQuery,
  useNotificationUpdateAllReadMutation,
} = notificationApi;
